import { slashCommandRegistry, type SlashArgSchema, type SlashCommandContext, type SlashCommandResult } from './registry'

function parseInput(input: string): { commandName: string; tokens: string[] } {
  const trimmed = input.trim()
  if (!trimmed.startsWith('/')) return { commandName: '', tokens: [] }

  const matches = Array.from(trimmed.matchAll(/"([^"]*)"|'([^']*)'|(\S+)/g))
  const tokens = matches.map(match => match[1] ?? match[2] ?? match[3] ?? '')
  const commandName = (tokens.shift() ?? '').replace(/^\//, '').toLowerCase()

  return { commandName, tokens }
}

function coerceArg(token: string, schema: SlashArgSchema): unknown {
  if ((schema.type === 'number' || schema.type === 'amount') && token.trim()) {
    const numeric = Number(token.replace(/[$,]/g, ''))
    return Number.isFinite(numeric) ? numeric : token
  }

  return token
}

function buildArgs(tokens: string[], args: SlashArgSchema[]): Record<string, unknown> {
  const parsed: Record<string, unknown> = {}

  args.forEach((arg, index) => {
    const raw = index === args.length - 1
      ? tokens.slice(index).join(' ')
      : tokens[index]

    if (raw == null || raw === '') return

    parsed[arg.name] = coerceArg(raw, arg)
  })

  return parsed
}

async function isAdmin(ctx: SlashCommandContext): Promise<boolean> {
  // Prefer the flag injected by ChatInterface (matches the @atrium.{run,bot,watch} email check)
  if (ctx.isAdmin !== undefined) return ctx.isAdmin

  const { data } = await ctx.supabase
    .from('workspace_members')
    .select('role')
    .eq('workspace_id', ctx.workspaceId)
    .eq('auth_user_id', ctx.userId)
    .single()

  return ['owner', 'admin'].includes(String((data as { role?: string } | null)?.role ?? ''))
}

export async function executeSlashCommand(
  input: string,
  ctx: SlashCommandContext
): Promise<SlashCommandResult> {
  const { commandName, tokens } = parseInput(input)
  if (!commandName) {
    return { kind: 'error', message: 'Type a slash command to run it.' }
  }

  const command = slashCommandRegistry.get(commandName)
  if (!command) {
    const [closest] = slashCommandRegistry.search(commandName, { includeAdmin: true })
    return {
      kind: 'error',
      message: closest
        ? `Unknown command /${commandName}. Did you mean /${closest.name}?`
        : `Unknown command /${commandName}.`,
    }
  }

  if (command.adminOnly && !(await isAdmin(ctx))) {
    return { kind: 'error', message: `You do not have permission to run /${command.name}.` }
  }

  const missing = command.args.find((arg, index) => arg.required && !tokens[index]?.trim())
  if (missing) {
    return { kind: 'error', message: `Missing required argument: ${missing.name}.` }
  }

  const args = buildArgs(tokens, command.args)

  if (command.tier === 'B') {
    const prompt = command.promptTemplate
      ? command.promptTemplate(args)
      : `Run slash command /${command.name} with arguments: ${tokens.join(' ') || '(none)'}`
    let displayMessage: string | undefined
    if (command.displayMessageBuilder) {
      const built = await command.displayMessageBuilder(args, ctx).catch(() => null)
      if (built != null) displayMessage = built
    }
    return { kind: 'llm-routed', prompt, ...(displayMessage ? { displayMessage } : {}) }
  }

  if (!command.handler) {
    return { kind: 'error', message: `/${command.name} is misconfigured (no handler).` }
  }

  return command.handler(args, ctx)
}
